import { useEffect } from "react";

/**
 * Global fix for SkyLoader spinners not animating when the OS has
 * prefers-reduced-motion enabled.
 *
 * The skypoint-ui library uses `motion-safe:animate-spin` which is a no-op
 * under reduced motion, and the library's own global reset forces
 * `animation-duration: .01s !important` on `*`.  No CSS rule can reliably
 * beat that combination across all browsers, so we apply inline styles
 * directly on spinner elements via a MutationObserver.
 *
 * Mount this once at the app root (e.g. inside AppLayout).
 */
export function SpinnerFix() {
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mq.matches) return; // no fix needed

    const STYLE = "animation: spin 1s linear infinite !important";

    function fixSpinners(root: ParentNode = document) {
      const spinners = root.querySelectorAll<HTMLElement>(
        '[role="progressbar"] .rounded-full.border-t-transparent',
      );
      for (const el of spinners) {
        el.style.cssText += STYLE;
      }
    }

    // Fix existing spinners
    fixSpinners();

    // Watch for new spinners being added to the DOM
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            // Check if the added node itself is a spinner or contains one
            if (
              node.matches?.('[role="progressbar"]') ||
              node.querySelector?.('[role="progressbar"]')
            ) {
              fixSpinners(node.parentNode ?? document);
            }
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return null;
}
